import { useMemo } from "react"
import { ChevronLeft, ChevronRight } from "lucide-react"
import { Button } from "../../components/ui/Button"

export function buildMonthCells(viewYear, viewMonth) {
  const firstOfMonth = new Date(viewYear, viewMonth, 1)
  const mondayOffset = (firstOfMonth.getDay() + 6) % 7
  const gridStart = new Date(firstOfMonth)
  gridStart.setDate(1 - mondayOffset)
  const cells = []
  for (let i = 0; i < 42; i++) {
    const d = new Date(gridStart)
    d.setDate(gridStart.getDate() + i)
    cells.push(d)
  }
  return cells
}

export function formatYmdFromDate(d) {
  const pad = (n) => String(n).padStart(2, "0")
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

/**
 * Mini calendrier mensuel (lundi = premier jour). Clic sur un jour pour choisir la date.
 */
export default function SessionMonthGrid({
  viewYear,
  viewMonth,
  onPrevMonth,
  onNextMonth,
  onPickDay,
  weekDayKeys,
  labels,
  weekdays,
  multiSelect = false,
  multiSelected = [],
  onToggleMultiDay,
}) {
  const cells = buildMonthCells(viewYear, viewMonth)
  const todayYmd = formatYmdFromDate(new Date())
  const selectedSet = useMemo(() => new Set(multiSelected), [multiSelected])
  const monthTitle = new Date(viewYear, viewMonth, 1).toLocaleDateString("fr-FR", {
    month: "long",
    year: "numeric",
  })

  return (
    <div className="w-full max-w-sm rounded-lg border border-border bg-card p-3 shadow-sm">
      <div className="mb-2 flex items-center justify-between gap-2">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-8 w-8 shrink-0 p-0"
          onClick={onPrevMonth}
          aria-label={labels.monthPrev}
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <span className="min-w-0 truncate text-center text-sm font-semibold capitalize text-foreground">
          {monthTitle}
        </span>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-8 w-8 shrink-0 p-0"
          onClick={onNextMonth}
          aria-label={labels.monthNext}
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
      <div className="grid grid-cols-7 gap-0.5 text-center text-[10px] font-medium text-muted-foreground sm:text-xs">
        {weekdays.map((w) => (
          <div key={w} className="px-0.5 py-1">
            {w}
          </div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-0.5">
        {cells.map((d) => {
          const ymd = formatYmdFromDate(d)
          const inMonth = d.getMonth() === viewMonth
          const inWeek = weekDayKeys.has(ymd)
          const isToday = ymd === todayYmd
          const isMultiPicked = multiSelect && selectedSet.has(ymd)
          return (
            <button
              key={`${ymd}-${d.getTime()}`}
              type="button"
              onClick={() =>
                multiSelect && onToggleMultiDay ? onToggleMultiDay(d) : onPickDay?.(d)
              }
              className={`
                flex h-8 w-full items-center justify-center rounded-md text-sm transition-colors
                ${!inMonth ? "text-muted-foreground/35 hover:bg-muted/40" : "text-foreground"}
                ${!multiSelect && inWeek && inMonth ? "bg-primary/15 font-semibold ring-1 ring-primary/35" : ""}
                ${multiSelect && isMultiPicked && inMonth ? "bg-primary font-semibold text-primary-foreground ring-2 ring-primary" : ""}
                ${isToday && inMonth && !isMultiPicked ? "ring-2 ring-primary" : ""}
                ${inMonth ? "hover:bg-primary/10" : ""}
              `}
            >
              {d.getDate()}
            </button>
          )
        })}
      </div>
      <p className="mt-2 text-center text-[11px] leading-snug text-muted-foreground">
        {labels.monthHint}
      </p>
    </div>
  )
}
